import React, { lazy, Suspense, useEffect, useRef } from 'react';

import type { LobbyLabProps } from './LobbyLab';
import { isLobbyLabOn, LAB_AVAILABLE, setLobbyLabFlag } from './lobbyLabState';

/**
 * The Lobby Lab's front door — the only thing outside this folder that may
 * import the panel. Same two locks as the SFX Lab (components/SfxLab/index.tsx
 * explains them at length): the build must be a lower environment or a dev
 * server, AND the operator must have set `localStorage.hijack_lobbylab = '1'`.
 * The panel itself is a lazy chunk, fetched only when both locks are open.
 *
 * What it is for: switching the lobby into the states a designer cannot reach
 * from a dev server — member header, promo banner, Your Tables strip, recent
 * tables, activity metrics — and adopting a real session from a lower
 * environment so the logged-in lobby can be inspected against live data.
 *
 * ON A DEVICE there is no address bar, so `?lobbylab=1` is out of reach: the
 * native app loads whatever `capacitor.config.ts` points it at, and a build
 * pointed at a deployed lower environment can never be handed the parameter.
 * Hence the tap gesture below — the same shape PerfHud already uses, so the
 * one on-device convention covers both dev tools.
 */
const LobbyLab = LAB_AVAILABLE ? lazy(() => import('./LobbyLab')) : null;

// Secret on-device opener: N taps in the LOBBY's BOTTOM-LEFT corner. Deliberate
// choices, each one avoiding a way this could misfire:
//   • BOTTOM-left, because PerfHud owns the TOP-left corner with the same
//     gesture (components/Debug/PerfHud.tsx) and the two must not both fire.
//   • Only while the lobby is the active view. The mount is app-wide, and at a
//     table the bottom-left corner can be a real action button — five taps
//     there must never be a poker decision. NOTE: utils/activeView's
//     `deriveActiveView` cannot answer this, because without the router's
//     table-route match it reports a `/table/...` path as `lobby` — precisely
//     the case this gate exists to catch — so the two non-lobby routes are
//     matched on the path directly.
//   • A per-tap gap rather than one tight total window, so the pace is
//     comfortable; pausing is what resets the streak.
//   • Passive and capture-phase: coordinates are only READ, and the control
//     under the corner keeps working normally (in the lobby that is the CASH
//     tab, which is already selected, so the taps are visually inert).
// Turning it back OFF is the panel's own "hide lab" button.
const CORNER_PX = 90;
const TAP_COUNT = 5;
const TAP_GAP_MS = 800;

/** The lobby is whatever is neither a table nor the grid (see the note above). */
const isLobbyPath = (pathname: string): boolean =>
  !pathname.startsWith('/table') && !pathname.startsWith('/grid');

const TapCornerToOpen = () => {
  const tapsRef = useRef<number[]>([]);

  useEffect(() => {
    const onTap = (event: PointerEvent) => {
      if (!isLobbyPath(window.location.pathname)) return;
      const inCorner =
        event.clientX <= CORNER_PX && event.clientY >= window.innerHeight - CORNER_PX;
      if (!inCorner) return;

      const now = Date.now();
      const taps = tapsRef.current;
      // Too long since the previous tap → this one starts a fresh streak.
      if (taps.length > 0 && now - taps[taps.length - 1] > TAP_GAP_MS) taps.length = 0;
      taps.push(now);
      if (taps.length < TAP_COUNT) return;

      taps.length = 0;
      // The flag is read at module load (see lobbyLabState), so it takes
      // effect on the next load — exactly like the panel's own hide button.
      setLobbyLabFlag(true);
      window.location.reload();
    };

    const options = { passive: true, capture: true } as const;
    window.addEventListener('pointerdown', onTap, options);
    return () => window.removeEventListener('pointerdown', onTap, { capture: true });
  }, []);

  return null;
};

export const LobbyLabMount = (props: LobbyLabProps) => {
  if (!LobbyLab) return null;
  if (!isLobbyLabOn()) return <TapCornerToOpen />;
  return (
    <Suspense fallback={null}>
      <LobbyLab {...props} />
    </Suspense>
  );
};

export default LobbyLabMount;
